import PaginatedList from '@theme/paginated-list';
import { isTabletBreakpoint, isDesktopBreakpoint, isMobileBreakpoint, startViewTransition, throttle, getStickyOffset, prefersReducedMotion } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { animate } from '@theme/vendor';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {

  /** Same horizontal insets as `util-card-media-sizes-attr.liquid`. */
  static #SIZES_INSET_DESKTOP = '100px'; // page padding reserved on the desktop tier (page-width)
  static #SIZES_INSET_BAND = '60px'; // tablet / laptop band
  static #SIZES_INSET_MOBILE = '32px';

  /** Sidebar slide animation duration (ms) — matches --animation-speed-slower in CSS. */
  static #SIDEBAR_DURATION = 350;
  /** Sidebar animation easing. */
  static #SIDEBAR_EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';

  /** @type {Function|null} */
  #scrollHandler = null;

  /** @type {number} */
  currentScrollTop = 0;

  #filtersSidebarAnimation = null;

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('initialized', '');

    this.#handleFragmentNavigation();
    this.#handleColumnSwitcher();

    this.currentScrollTop = window.scrollY;

    this.#scrollHandler = throttle(() => this.#onScroll(false));
    window.addEventListener('scroll', this.#scrollHandler, { passive: true });
    this.#onScroll(true);

    // Listen for filter updates to scroll to top. Own private arrow field —
    // distinct from PaginatedList's #handleFilterUpdate, so no inheritance clash.
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    // Cancel any in-flight sidebar animation so its finished callback doesn't run
    // on a detached element.
    this.#filtersSidebarAnimation?.cancel();
    this.#filtersSidebarAnimation = null;
  }

  /**
   * Updates the layout when column switcher value changes.
   * @param {Event} event - Event with target input element
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Animates layout change with view transition (desktop/tablet) or direct change (mobile).
   * @param {string} value - Number of columns to display
   */
  #animateLayoutChange = async (value) => {
    const productGrid = this.getRef('productGrid');

    if (!productGrid) return;

    if (isMobileBreakpoint()) {
      this.#setLayout(value);
    } else {
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    }
  };

  /**
   * Sets CSS custom properties for column layout based on current breakpoint.
   * @param {string} value - Number of columns to display
   */
  #setLayout(value) {
    const productGrid = this.getRef('productGrid');
    if (!productGrid) return;

    if (isMobileBreakpoint()) {
      productGrid.style.setProperty('--columns-mobile', value);
      const promoCard = productGrid.querySelector('.promo-card');
      if (promoCard?.getAttribute('data-card-columns-mobile') == 1) {
        promoCard?.style.setProperty('--columns-mobile', 1);
      } else {
        promoCard?.style.setProperty('--columns-mobile', value);
      }
      this.#setCartAttribute(value, 'mobile');
      this.#setImageSizes('', '', value);
    } else if (isTabletBreakpoint()) {
      productGrid.style.setProperty('--columns-tablet', value);
      this.#setCartAttribute(value, 'tablet');
      this.#setImageSizes('', value, '');
    } else if (isDesktopBreakpoint()) {
      productGrid.style.setProperty('--columns', value);
      this.#setCartAttribute(value, 'desktop');
      this.#setImageSizes(value, '', '');
    }

  }

  /**
   * Returns the CSS width value used in `sizes` / `calc`: `min(100vw, …)` when inside `.results-list-page-width`, else `100vw`.
   * @returns {string}
   */
  #getPageWidth() {
    const section = this.closest('.shopify-section');
    const pageWidth = section?.querySelector('.page-width');
    if (pageWidth) {
      const pageWidthValue = getComputedStyle(pageWidth).getPropertyValue('--page-width');
      return `min(100vw, ${pageWidthValue})`;
    }
    return '100vw';
  }

  /**
   * Whether the grid sits in a page-width / page-full-width section (Liquid `is_page_width`),
   * which reserves the 100px page padding on the desktop tier.
   * @returns {boolean}
   */
  #isPageWidth() {
    return !!this.closest('.shopify-section')?.querySelector('.page-width, .page-full-width');
  }

  /**
   * Returns the slot width expression string used *inside* `calc(...)` in `sizes`.
   * When the promo card spans 2 columns, appends ` * 2` (Liquid `promo_columns == 2`).
   * @param {string} expression - e.g. `min(100vw, 1200px) / 4` or `(100vw - 100px) / 3`
   * @param {number|undefined} promoColumnSpan
   * @returns {string}
   */
  #getSlotCalcExpression(expression, promoColumnSpan) {
    if (promoColumnSpan !== 2) return expression;
    return `${expression} * ${promoColumnSpan}`;
  }

  /**
   * Builds the `sizes` attribute for grid images (same rules as `util-card-media-sizes-attr.liquid`).
   * @param {Object} options
   * @param {string|number} options.columns - Desktop column count
   * @param {string|number} [options.columnsTablet]
   * @param {string|number} [options.columnsLaptop]
   * @param {string|number} [options.columnsMobile]
   * @param {number} [options.promoColumns] - Promo column span (when `2`, slot width is doubled in the formula)
   * @param {number} [options.promoColumnsMobile]
   * @returns {string}
   */
  #buildImageSizes({
    columns,
    columnsTablet,
    columnsLaptop,
    columnsMobile,
    promoColumns,
    promoColumnsMobile,
  } = {}) {
    const columnsDesktop = String(columns).trim();
    // Fall back to the desktop count when tablet/laptop are missing (Liquid `| default: columns`).
    const columnsTabletSafe = String(columnsTablet || '').trim() || columnsDesktop;
    const columnsLaptopSafe = String(columnsLaptop || '').trim() || columnsDesktop;

    const pageWidth = this.#getPageWidth().trim() || '100vw';
    // page-width / page-full-width reserve the 100px page padding on the desktop tier.
    const desktopWidth = this.#isPageWidth()
      ? `(${pageWidth} - ${ResultsList.#SIZES_INSET_DESKTOP})`
      : pageWidth;

    const desktopSlot = this.#getSlotCalcExpression(`${desktopWidth} / ${columnsDesktop}`, promoColumns);
    const laptopSlot = this.#getSlotCalcExpression(`(100vw - ${ResultsList.#SIZES_INSET_BAND}) / ${columnsLaptopSafe}`, promoColumns);
    const tabletSlot = this.#getSlotCalcExpression(`(100vw - ${ResultsList.#SIZES_INSET_BAND}) / ${columnsTabletSafe}`, promoColumns);

    let mobileSlot = `(100vw - ${ResultsList.#SIZES_INSET_MOBILE}) / ${columnsMobile}`;
    if (promoColumnsMobile === 2 && columnsMobile === '2') {
      mobileSlot = `${mobileSlot} * ${promoColumnsMobile}`;
    }

    // Fixed tiers, largest min-width first (matches util-card-media-sizes-attr.liquid).
    const rules = [
      `(min-width: 1280px) calc(${desktopSlot})`,
      `(min-width: 1025px) calc(${laptopSlot})`,
      `(min-width: 768px) calc(${tabletSlot})`,
    ];
    if (columnsMobile !== '') {
      rules.push(`calc(${mobileSlot})`);
    }
    return rules.join(', ');
  }

  /**
   * Updates image sizes attribute based on current column layout.
   * @param {string} desktopColumn - Desktop column count (empty = read from grid)
   * @param {string} tabletColumn - Tablet column count
   * @param {string} mobileColumn - Mobile column count
   */
  #setImageSizes(desktopColumn, tabletColumn, mobileColumn) {
    const productGrid = this.getRef('productGrid');
    if (!productGrid) return;

    const columnsDesktop =
      desktopColumn || productGrid.style.getPropertyValue('--columns');
    const columnsTablet =
      tabletColumn || productGrid.style.getPropertyValue('--columns-tablet');
    // Laptop is independent of the switcher (which only sets desktop/tablet/mobile); read the
    // grid's own --columns-laptop so sizes track the visual var(--columns-laptop, --columns).
    const columnsLaptop = productGrid.style.getPropertyValue('--columns-laptop');
    const columnsMobile =
      mobileColumn || productGrid.style.getPropertyValue('--columns-mobile');

    const productImageSizes = this.#buildImageSizes({
      columns: columnsDesktop,
      columnsTablet: columnsTablet,
      columnsLaptop: columnsLaptop,
      columnsMobile: columnsMobile,
    });

    productGrid.querySelectorAll('.product-media__image').forEach((image) => {
      const img = image.querySelector('img');
      if (!img) return;
      img.setAttribute('sizes', productImageSizes);
    });

    const promoCardImages = this.querySelectorAll('.promo-card__media');
    if (!promoCardImages) return;
    promoCardImages.forEach((promoCardImage) => {
      const img = promoCardImage.querySelector('.promo-card__image img');
      if (!img) return;
      const promoCard = promoCardImage.closest('.promo-card');
      const promoCardColumnSpan = parseInt(promoCard.getAttribute('data-card-columns'));
      const promoCardColumnSpanMobile = parseInt(promoCard.getAttribute('data-card-columns-mobile'));
      const promoImageSizes = this.#buildImageSizes({
        columns: columnsDesktop,
        columnsTablet: columnsTablet,
        columnsLaptop: columnsLaptop,
        columnsMobile: columnsMobile,
        promoColumns: promoCardColumnSpan,
        promoColumnsMobile: promoCardColumnSpanMobile,
      });
      img.setAttribute('sizes', promoImageSizes);
    });
  }

  /**
   * Toggles filters sidebar visibility using JS-driven animation.
   * Animates width + paddingInlineEnd + opacity together to avoid CSS transition flicker.
   * @param {Event} event - Click event from filter toggle button
   */
  toggleFiltersSidebar(event) {
    event.preventDefault();
    const filtersSidebar = this.getRef('filtersSidebar');
    if (!filtersSidebar) return;

    const hiddenClass = 'filters-sidebar--hidden';
    const shouldOpen = !filtersSidebar.classList.contains('show-sidebar');
    const toggleButton = event.target.closest('.facets-toggle__button');
    if (toggleButton) {
      toggleButton.classList.toggle('show-sidebar', shouldOpen);
      toggleButton.setAttribute('aria-expanded', String(shouldOpen));
    }

    this.#filtersSidebarAnimation?.cancel();
    this.#filtersSidebarAnimation = null;

    // Respect reduced motion — skip animation
    if (prefersReducedMotion()) {
      if (shouldOpen) {
        filtersSidebar.classList.remove(hiddenClass);
        filtersSidebar.classList.add('show-sidebar');
      } else {
        filtersSidebar.classList.remove('show-sidebar');
        filtersSidebar.classList.add(hiddenClass);
      }
      return;
    }

    const animOpts = {
      duration: ResultsList.#SIDEBAR_DURATION,
      easing: ResultsList.#SIDEBAR_EASING,
      fill: 'forwards',
    };

    if (shouldOpen) {
      const targetWidth = parseFloat(getComputedStyle(filtersSidebar).getPropertyValue('--filters-width')) || 270;
      const targetHeight = filtersSidebar.scrollHeight;

      filtersSidebar.classList.remove(hiddenClass);

      const anim = animate(filtersSidebar, [
        { width: '0px', height: '0px', opacity: 0 },
        { width: `${targetWidth}px`, height: `${targetHeight}px`, opacity: 1 },
      ], animOpts);

      anim.finished
        .then(() => {
          if (this.#filtersSidebarAnimation !== anim) return;
          filtersSidebar.classList.remove(hiddenClass);
          filtersSidebar.classList.add('show-sidebar');
          anim.cancel();
          this.#filtersSidebarAnimation = null;
        })
        // Cancelling the animation rejects `finished`; swallow it so an
        // interrupted toggle doesn't surface an unhandled rejection.
        .catch(() => {});

      this.#filtersSidebarAnimation = anim;
    } else {
      const { width: currentWidth, height: currentHeight } = filtersSidebar.getBoundingClientRect();

      filtersSidebar.classList.remove('show-sidebar');
      filtersSidebar.style.height = `${currentHeight}px`;

      const anim = animate(filtersSidebar, [
        { width: `${currentWidth}px`, opacity: 1 },
        { width: '0px', opacity: 0 },
      ], animOpts);

      anim.finished
        .then(() => {
          if (this.#filtersSidebarAnimation !== anim) return;
          filtersSidebar.classList.remove('show-sidebar');
          filtersSidebar.classList.add(hiddenClass);
          filtersSidebar.style.height = '';
          anim.cancel();
          this.#filtersSidebarAnimation = null;
        })
        // Cancelling the animation rejects `finished`; swallow it so an
        // interrupted toggle doesn't surface an unhandled rejection.
        .catch(() => {});

      this.#filtersSidebarAnimation = anim;
    }
  }

  /**
   * Re-syncs image `sizes` after a page is appended/prepended. New cards come
   * from server-rendered HTML that may carry stale-column `sizes` (prefetched
   * cache + non-awaited cart persistence), so without this they ignore the layout
   * the user picked via the column switcher and the browser loads wrong-sized
   * images. Empty args = read the grid's current CSS column vars.
   * @override
   */
  afterPageRender() {
    this.#setImageSizes('', '', '');
  }

  /**
   * Loads next page of products with loading state.
   * @param {Event} event - Click event from load more button
   */
  async loadMore(event) {
    event.preventDefault();
    const btn = event.target;
    btn.classList.add('btn--loading');
    await this.renderNextPage();
    btn.classList.remove('btn--loading');
  }

  /**
   * Synchronizes column switcher UI with current CSS column values
   * Sets checked state on switcher inputs based on product grid CSS variables
   * Removes transition-none class after a short delay to enable animations
   */
  #handleColumnSwitcher() {
    const columnSwitcherDesktop = this.getRef('columnSwitcherDesktop');
    const columnSwitcherTablet = this.getRef('columnSwitcherTablet');
    const columnSwitcherMobile = this.getRef('columnSwitcherMobile');
    const productGrid = this.getRef('productGrid');

    if (!productGrid) return;

      const switchers = [
        { element: columnSwitcherMobile, property: '--columns-mobile' },
        { element: columnSwitcherTablet, property: '--columns-tablet' },
        { element: columnSwitcherDesktop, property: '--columns' }
      ];

      switchers.forEach(({ element, property }) => {
        if (!element) return;

        const checkedInput = element.querySelector('input:checked');
        if (!checkedInput) {
          const columns = productGrid.style.getPropertyValue(property);
          const targetInput = element.querySelector(`input[value="${columns}"]`);
          if (targetInput) {
            targetInput.checked = true;
          }
        }

        setTimeout(() => {
          element.querySelectorAll('.column-picker').forEach(picker => {
            picker.classList.remove('!transition-none');
          });
        }, 10);
      });
  }

  /**
   * Handles scroll events for sticky toolbar behavior
   * @param {boolean} initial - Whether this is the initial call
   */
  #onScroll(initial = false) {
    const collectionToolbar = this.querySelector('[ref="collectionToolbar"]');
    if (!collectionToolbar || !collectionToolbar.classList.contains('sticky-mobile')) return;
    if (!isMobileBreakpoint()) return;

    const scrollTop = window.scrollY;

    if (!initial && scrollTop === this.currentScrollTop) {
      return;
    }

    requestAnimationFrame(() => {
      const toolbarBounds = collectionToolbar.getBoundingClientRect();
      const toolbarComputedStyle = getComputedStyle(collectionToolbar);
      const toolbarTopCSS = parseFloat(toolbarComputedStyle.top) || 0;

      const isSticky = Math.abs(toolbarBounds.top - toolbarTopCSS) <= 1;

      if (isSticky && scrollTop > 0) {
        collectionToolbar.classList.add('toolbar-scrolling');
      } else {
        collectionToolbar.classList.remove('toolbar-scrolling');
      }

      this.currentScrollTop = scrollTop;
    });
  }

  /**
   * Handles navigation to product cards using URL hash fragments.
   */
  #handleFragmentNavigation() {
    const url = new URL(window.location.href);
    if (url.hash) {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          const card = document.getElementById(url.hash.slice(1));
          if (card) {
            setTimeout(() => {
              card.scrollIntoView({ behavior: 'smooth' });
            }, 1000);
          }
        },
        { once: true }
      );
    }
  }

  /**
   * Updates cart attributes with column layout for breakpoint persistence.
   * @param {string} columns - Number of columns for breakpoint
   * @param {string} breakpoint - Breakpoint name ('mobile', 'tablet', 'desktop')
   */
  #setCartAttribute = (columns, breakpoint) => {
    if (Shopify.designMode) {
      return;
    }
    const attributeProperty = `products_grid_columns_${breakpoint}`;
    fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: {
          [attributeProperty]: columns
        }
      }),
      keepalive: true
    });
  };

  /**
   * Handles filter updates by scrolling to top
   */
  #handleFilterUpdate = () => {
    const elementTop = this.getBoundingClientRect().top + window.scrollY;
    const stickyOffset = getStickyOffset();

    window.scrollTo({
      top: elementTop - stickyOffset,
      behavior: 'smooth'
    });
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
